import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import {
  Controller,
  useForm,
} from "react-hook-form";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import { toast } from "sonner";
import { z } from "zod";

import {
  extractAuthenticatedUser,
  extractAuthToken,
  login,
} from "../../../services/AuthenticationService";

import { useAuthStore } from "../../../stores/authStore";
import { getApiErrorMessage } from "../../../utils/getApiErrorMessage";

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .email("Enter a valid email address."),

  password: z
    .string()
    .min(6, "Use at least 6 characters."),
});

export function LoginPage() {
  const [showPassword, setShowPassword] =
    useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const signIn = useAuthStore(
    (state) => state.signIn,
  );

  const {
    control,
    handleSubmit,
    setError,
    formState: {
      errors,
      isSubmitting,
    },
  } = useForm({
    resolver: zodResolver(loginSchema),

    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      const result = await login(values);

      console.log(
        "LOGIN API RESULT:",
        result,
      );

      const token =
        extractAuthToken(result);

      const user =
        extractAuthenticatedUser(result);

      console.log(
        "EXTRACTED TOKEN:",
        token,
      );

      console.log(
        "EXTRACTED USER:",
        user,
      );

      if (!token) {
        throw new Error(
          "The API did not return an authentication token.",
        );
      }

      if (!user) {
        throw new Error(
          "The API did not return the authenticated user.",
        );
      }

      signIn({
        user,
        token,
      });

      console.log(
        "AUTH STORE AFTER LOGIN:",
        useAuthStore.getState(),
      );

      toast.success(
        result?.message ||
          "Signed in successfully.",
      );

      const destination =
        location.state?.from?.pathname ||
        "/";

      navigate(destination, {
        replace: true,
      });
    } catch (error) {
      console.error(
        "LOGIN ERROR:",
        error,
      );

      if (
        error?.response?.status === 422
      ) {
        const validationErrors =
          error.response.data?.errors ||
          {};

        Object.entries(
          validationErrors,
        ).forEach(
          ([field, messages]) => {
            if (
              field === "email" ||
              field === "password"
            ) {
              setError(field, {
                type: "server",

                message:
                  Array.isArray(
                    messages,
                  )
                    ? messages[0]
                    : String(
                        messages,
                      ),
              });
            }
          },
        );
      }

      toast.error(
        getApiErrorMessage(
          error,
          "Unable to sign in.",
        ),
      );
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        bgcolor:
          "background.default",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 380,
          p: 3,
          border: "1px solid",
          borderColor: "divider",
          borderTop: "4px solid",
          borderTopColor:
            "primary.main",
        }}
      >
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="overline"
            sx={{
              display: "block",
              color: "primary.main",
              fontWeight: 800,
            }}
          >
            SPEND2CONNECT
          </Typography>

          <Typography
            component="h1"
            sx={{
              fontSize: "1.35rem",
              fontWeight: 800,
            }}
          >
            Sign in
          </Typography>

          <Typography
            sx={{
              color:
                "text.secondary",
              fontSize: "0.78rem",
            }}
          >
            WiFi voucher management
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(
            onSubmit,
          )}
          noValidate
        >
          <Stack spacing={1.75}>
            <Controller
              name="email"
              control={control}
              render={({
                field,
              }) => (
                <TextField
                  {...field}
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  fullWidth
                  size="small"
                  error={Boolean(
                    errors.email,
                  )}
                  helperText={
                    errors.email
                      ?.message
                  }
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({
                field,
              }) => (
                <TextField
                  {...field}
                  label="Password"
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  autoComplete="current-password"
                  fullWidth
                  size="small"
                  error={Boolean(
                    errors.password,
                  )}
                  helperText={
                    errors.password
                      ?.message
                  }
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon />
                        </InputAdornment>
                      ),

                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            type="button"
                            edge="end"
                            size="small"
                            onMouseDown={(
                              event,
                            ) =>
                              event.preventDefault()
                            }
                            onClick={() =>
                              setShowPassword(
                                (
                                  current,
                                ) =>
                                  !current,
                              )
                            }
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon />
                            ) : (
                              <VisibilityOutlinedIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={
                isSubmitting
              }
            >
              {isSubmitting ? (
                <>
                  <CircularProgress
                    size={18}
                    color="inherit"
                    sx={{ mr: 1 }}
                  />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}